<template>
  <div class="me">
    <!-- 背景 -->
    <div class="bg" aria-hidden="true">
      <div class="grid"></div>
      <div class="noise"></div>
      <div class="glow g1"></div>
      <div class="glow g2"></div>
      <div class="glow g3"></div>
    </div>

    <AppHeader />

    <main class="main">
      <!-- 页面标题 -->
      <section class="page-hd">
        <div class="ttl">{{ $t("me.page.title") }}</div>
        <div class="sub">{{ $t("me.page.sub") }}</div>
      </section>

      <!-- 账号概览 -->
      <section class="panel hero">
        <div class="hero-top">
          <div class="hl">
            <div class="k">{{ $t("me.hero.levelK") }}</div>
            <div class="lv">
              <span class="badge">{{ myLevel }}</span>
              <span class="muted small">{{ $t("me.hero.teamTier") }}</span>

              <span v-if="nodeMember" class="node-badge">
                <span class="node-dot"></span>
                {{ $t("me.hero.nodeMember") }}
              </span>
            </div>

            <div class="k mt">{{ $t("me.hero.depthK") }}</div>
            <div class="v">
              <b>{{ eligibleDepth }}</b>
              <span class="muted small">{{ $t("me.hero.depthUnit") }}</span>
              <span class="sep">/</span>
              <span class="muted small">{{ $t("me.hero.depthMax", { n: 16 }) }}</span>
            </div>
          </div>

          <div class="hr">
            <div class="k">{{ $t("me.hero.addrK") }}</div>
            <div class="addr mono">
              {{ shortAddr(myAddress) }}
              <button class="copy" type="button" @click="copy(myAddress)">
                {{ $t("common.copy") }}
              </button>
            </div>

            <div class="stats">
              <div class="sitem">
                <div class="sk">{{ $t("me.hero.holdPerf") }}</div>
                <div class="sv up">
                  {{ fmt(myHoldPerf) }} <span class="unit">USDT</span>
                </div>
              </div>
              <div class="sitem">
                <div class="sk">{{ $t("me.hero.teamPerf") }}</div>
                <div class="sv">
                  {{ fmt(myTeamPerf) }} <span class="unit">USDT</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ✅ 无 token 提示 -->
        <div v-if="!hasToken" class="ov-hint muted">
          {{ $t("me.hint.needConnect") }}
        </div>

        <!-- 升级进度（小区业绩） -->
        <div class="prog">
          <div class="prog-top">
            <div class="pt">
              <span class="k">
                {{ $t("me.progress.toNext", { lv: nextLevel.name }) }}
              </span>
              <span class="muted small">{{ $t("me.progress.label") }}</span>
            </div>

            <div class="pv">
              <b>{{ fmt(minAreaPerf) }}</b>
              <span class="muted">/</span>
              <span>{{ fmt(nextLevel.req) }}</span>
              <span class="unit">USDT</span>
            </div>
          </div>

          <div class="bar">
            <div class="bar-in" :style="{ width: progressPct + '%' }"></div>
          </div>

          <div class="prog-foot">
            <div class="muted small">
              {{ $t("me.progress.needMorePrefix") }}
              <b class="em">{{ fmt(needMore) }}</b>
              USDT
            </div>

            <!-- <div class="pill2">
              {{ $t("me.progress.peerReward") }} <b>10%</b>
            </div> -->
          </div>
        </div>
      </section>

      <!-- 邀请链接 -->
      <section class="panel invite" :class="{ locked: !canInvite }">
  <template v-if="canInvite">
    <div class="inv-top">
      <div>
        <div class="st">{{ $t("me.invite.title") }}</div>
        <div class="muted small">{{ $t("me.invite.sub") }}</div>
      </div>

      <button class="btn small primary" type="button" :disabled="!inviteLink" @click="copy(inviteLink)">
        {{ $t("common.copy") }}
      </button>
    </div>

    <div class="inv-box mono">
      {{ inviteLink || $t("common.placeholder") }}
    </div>
  </template>

  <div class="inviteLocked" v-else>
    <div class="inviteLockIcon">🔒</div>

    <div class="inviteLockBody">
      <div class="inviteLockTitle">{{ $t("home.invite.lockedTitle") }}</div>
      <div class="inviteLockSub">
        {{ $t("home.invite.lockedSub") }}
      </div>
    </div>

    <button class="btn small primary" type="button" @click="$router.push('/position')">
      {{ $t("home.invite.createDelegation") }}
    </button>
  </div>
</section>

      <!-- 直推列表 -->
      <section class="sec">
        <div class="sec-head">
          <div class="st">{{ $t("me.list.title") }}</div>
          <div class="right muted">{{ $t("me.list.sub") }}</div>
        </div>

        <section class="panel list">
          <!-- ✅ loading skeleton -->
          <div v-if="loadingList" class="skeleton">
            <div class="sk" v-for="i in 4" :key="i"></div>
          </div>

          <div v-else>
            <div v-if="!referrals.length" class="empty muted">
              {{ hasToken ? $t("common.empty") : $t("me.list.needConnectEmpty") }}
            </div>

            <div class="row" v-for="u in referrals" :key="u.address">
              <!-- ✅ 未展开：地址 + 等级 + 注册时间 -->
              <button class="row-main compact" type="button" @click="toggle(u.address)">
                <div class="c1">
                  <div class="label muted small">{{ $t("me.list.addr") }}</div>
                  <div class="val mono">{{ shortAddr(u.address) }}</div>
                </div>

                <div class="cL">
                  <div class="label muted small">{{ $t("me.list.teamLevel") }}</div>
                  <div class="val">
                    <span class="badge mini">{{ u.teamLevel || "S0" }}</span>
                  </div>
                </div>

                <div class="c2">
                  <div class="label muted small">{{ $t("me.list.reg") }}</div>
                  <div class="val mono muted">{{ u.regAt || $t("common.placeholder") }}</div>
                </div>

                <i class="caret" :class="{ up: opened === u.address }"></i>
              </button>

              <!-- 展开：核心信息 -->
              <div class="row-more" v-if="opened === u.address">
                <div class="more-core">
                  <div class="kline">
                    <div class="k">{{ $t("me.list.holdPerf") }}</div>
                    <div class="v up">
                      {{ fmt(u.holdPerf) }} <span class="unit">USDT</span>
                    </div>
                  </div>
                  <div class="kline">
                    <div class="k">{{ $t("me.list.teamPerf") }}</div>
                    <div class="v">
                      {{ fmt(u.teamPerf) }} <span class="unit">USDT</span>
                    </div>
                  </div>
                </div>

                <div class="more-grid">
                  <!-- ✅ 原“团队等级”位置：改为团队人数 clusterUserCount -->
                  <div class="mcard">
                    <div class="mk">{{ $t("me.list.clusterUsers") }}</div>
                    <div class="mv">
                      {{ Number(u.clusterUserCount || 0) }}
                      <span class="unit">{{ $t("me.list.people") }}</span>
                    </div>
                  </div>

                  <div class="mcard">
                    <div class="mk">{{ $t("me.list.minArea") }}</div>
                    <div class="mv">
                      {{ fmt(u.minAreaPerf) }} <span class="unit">USDT</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ✅加载更多：真正分页 -->
            <div class="loadmore">
              <div class="muted small">
                {{ $t("me.list.shown", { n: referrals.length, total: total }) }}
              </div>

              <button class="btn lm" type="button" :disabled="noMore || loadingMore" @click="loadMore">
                {{
                  noMore
                    ? $t("common.noMore")
                    : loadingMore
                      ? $t("me.action.loading")
                      : $t("common.loadMore")
                }}
              </button>
            </div>
          </div>
        </section>
      </section>

      <FooterLite />
    </main>

    <BottomTab />
  </div>
</template>

<script>
import AppHeader from "@/components/AppHeader.vue";
import FooterLite from "@/components/FooterLite.vue";
import BottomTab from "@/components/BottomTab.vue";

import wallet from "@/store/wallet";
import authReadyMixin from "@/mixins/authReady";

// ✅ 你已经创建好的 api/me.js
import { fetchMeOverview, fetchMeReferrals } from "@/api/me";

export default {
  name: "Me",
  mixins: [authReadyMixin],
  components: { AppHeader, FooterLite, BottomTab },

  data() {
    return {
      // === overview ===
      myAddress: "",
      myLevel: "S0",
      nodeMember: false,
      myHoldPerf: 0,
      myTeamPerf: 0,
      minAreaPerf: 0,
      directCount: 0,
      inviteLink: "",
      canInvite: false,
      eligibleDepthFromServer: null, // 服务端如果直接给，就用它

      // === list ===
      referrals: [],
      page: 1,
      size: 6,
      total: 0,

      opened: "",

      // === flags ===
      loadingOverview: false,
      loadingList: false,
      loadingMore: false,
      loadedOnce: false // ✅ 防止返回重复加载
    };
  },

  computed: {
    hasToken() {
      return !!(wallet && wallet.state && wallet.state.token);
    },

    // ✅ 优先用后端返回 eligibleDepth（如果有），否则用你规则计算
    eligibleDepth() {
      if (!this.hasToken) return 0;
      if (this.eligibleDepthFromServer !== null && this.eligibleDepthFromServer !== undefined) {
        return Number(this.eligibleDepthFromServer || 0);
      }
      // 规则：直推 1 人拿 2 代；直推 5 人拿 10 代
      if (Number(this.directCount || 0) >= 5) return 10;
      if (Number(this.directCount || 0) >= 1) return 2;
      return 1;
    },

    levelTable() {
      return [
        { name: "S1", req: 5000, ratio: 10 },
        { name: "S2", req: 20000, ratio: 20 },
        { name: "S3", req: 50000, ratio: 30 },
        { name: "S4", req: 150000, ratio: 40 },
        { name: "S5", req: 500000, ratio: 50 },
        { name: "S6", req: 1500000, ratio: 55 },
        { name: "S7", req: 5000000, ratio: 60 }
      ];
    },

    nextLevel() {
      const idx = this.levelTable.findIndex((x) => x.name === this.myLevel);
      if (idx < 0) return this.levelTable[0];
      return this.levelTable[Math.min(idx + 1, this.levelTable.length - 1)];
    },

    progressPct() {
      const req = Number(this.nextLevel.req || 0);
      if (!req) return 0;
      const v = Math.max(0, Math.min(100, (Number(this.minAreaPerf || 0) / req) * 100));
      return v;
    },

    needMore() {
      const req = Number(this.nextLevel.req || 0);
      return Math.max(0, req - Number(this.minAreaPerf || 0));
    },

    noMore() {
      // total 为 0 时，退化用长度判断
      if (Number(this.total || 0) > 0) return this.referrals.length >= this.total;
      return this.referrals.length < this.size ? true : false;
    }
  },

  mounted() {
    this.tryLoadOnce(false);
  },

  activated() {
    // ✅ keep-alive 返回也不会二次加载
    this.tryLoadOnce(false);
  },

  methods: {
    // authReadyMixin 回调：token 就绪后触发
    onAuthReady() {
      // token 刚准备好：强制刷新一次
      this.tryLoadOnce(true);
    },

    tryLoadOnce(force) {
      force = !!force;
      if (!this.hasToken) return; // ✅ 无 token 不加载
      if (this.loadingOverview || this.loadingList || this.loadingMore) return;
      if (this.loadedOnce && !force) return;

      this.loadedOnce = true;
      this.loadAll(true); // 首次强制刷新
    },

    // ✅ 解包 R.ok({data}) / axios res.data
    unwrap(res) {
      if (res && res.data && typeof res.data === "object") {
        if ("data" in res.data) return res.data.data;
        return res.data;
      }
      if (res && typeof res === "object" && "data" in res) return res.data;
      return res;
    },

    async loadAll(silent) {
      silent = !!silent;
      if (!this.hasToken) return;

      this.loadingOverview = true;
      this.loadingList = true;

      try {
        // 1) overview 一次拿齐
        const oRes = await fetchMeOverview({ silent: silent });
        const ov = this.unwrap(oRes) || {};

        // 你后端推荐结构：{ profile, area, invite }
        const profile = ov.profile || ov || {};
        const area = ov.area || {};
        const invite = ov.invite || {};
        this.nodeMember = !!ov.nodeMember;

        this.myAddress = String(profile.address || profile.userCode || "");
        this.myLevel = this.mapLevel(profile.userLevel, profile.levelName);
        this.myHoldPerf = Number(profile.selfPerformanceUsd || profile.selfPerf || profile.myHoldPerf || 0);
        this.myTeamPerf = Number(profile.teamPerformanceUsd || profile.teamPerf || profile.myTeamPerf || 0);

        this.minAreaPerf = Number(area.minAreaPerf || area.areaPerf || area.minArea || 0);

        this.directCount = Number(invite.directCount || invite.direct || 0);
        this.canInvite = !!invite.canInvite;
        this.inviteLink = this.canInvite ? String(invite.inviteLink || invite.url || "") : "";

        if (invite.eligibleDepth !== undefined && invite.eligibleDepth !== null) {
          this.eligibleDepthFromServer = Number(invite.eligibleDepth || 0);
        }

        // 2) list reset + 拉第一页
        this.page = 1;
        this.total = Number(ov.total || 0);
        await this.loadReferrals(true, true);
      } catch (e) {
        if (!silent) this.toast((e && e.message) || this.$t("me.toast.loadFail"));
      } finally {
        this.loadingOverview = false;
        this.loadingList = false;
      }
    },

    async loadReferrals(reset, silent) {
      reset = !!reset;
      silent = !!silent;

      if (!this.hasToken) return;

      const page = reset ? 1 : this.page;
      const size = this.size;

      const rRes = await fetchMeReferralsSafe(page, size, silent, this);

      const vo = rRes || {};
      const list = vo.records || vo.list || vo.items || (Array.isArray(vo) ? vo : []);
      const total = Number(vo.total || 0);

      const mapped = (list || []).map(this.mapReferral);

      if (reset) this.referrals = mapped;
      else this.referrals = this.referrals.concat(mapped);

      if (total) this.total = total;
      else if (!total && reset) this.total = this.referrals.length;

      this.opened = "";
    },

    async loadMore() {
      if (!this.hasToken) return this.toast(this.$t("me.toast.needConnect"));
      if (this.noMore) return;
      if (this.loadingMore || this.loadingList) return;

      this.loadingMore = true;
      try {
        this.page = this.page + 1;
        await this.loadReferrals(false, true);
      } catch (e) {
        this.page = Math.max(1, this.page - 1);
        this.toast((e && e.message) || this.$t("me.toast.loadFail"));
      } finally {
        this.loadingMore = false;
      }
    },

    toggle(addr) {
      this.opened = this.opened === addr ? "" : addr;
    },

    mapLevel(userLevel, levelName) {
      if (levelName && String(levelName).startsWith("S")) return String(levelName);
      const n = Number(userLevel || 0);
      if (!n || n <= 0) return "S0";
      return "S" + n;
    },

    mapReferral(r) {
      r = r || {};
      return {
        address: String(r.address || r.userCode || ""),
        regAt: this.fmtDate(r.regAt || r.createdAt || r.registerAt || ""),
        holdPerf: Number(r.selfPerformanceUsd || r.holdPerf || r.selfPerf || 0),
        teamPerf: Number(r.teamPerformanceUsd || r.teamPerf || 0),
        teamLevel: this.mapLevel(r.userLevel, r.levelName),
        minAreaPerf: Number(r.minAreaPerf || r.areaPerf || 0),

        // ✅ 新增：团队人数
        clusterUserCount: Number(r.clusterUserCount || r.teamUserCount || r.teamUsers || 0)
      };
    },

    fmtDate(v) {
      const s = String(v || "");
      if (!s) return "";
      if (s.includes("T")) return s.replace(/-/g, "/").slice(0, 10);
      if (s.includes("-")) return s.replace(/-/g, "/").slice(0, 10);
      return s.slice(0, 10);
    },

    fmt(n) {
      return Number(n || 0).toFixed(2);
    },

    shortAddr(a) {
      return a ? a.slice(0, 2) + "..." + a.slice(-3) : "";
    },

    copy(text) {
      const t = String(text || "");
      if (!t) return;

      const el = document.createElement("textarea");
      el.value = t;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      // ✅ i18n
      if (this.$toast && this.$toast.success) this.$toast.success(this.$t("common.copied"));
      else this.toast(this.$t("common.copied"));
    },

    toast(msg) {
      this.$toast && this.$toast.show ? this.$toast.show(msg) : alert(msg);
    }
  }
};

/**
 * ✅ 保持你原来的 safe wrapper 不动
 */
async function RqUnwrap(res, vm) {
  const d = vm.unwrap(res);
  return d || {};
}

async function fetchMeR(vm, page, size, silent) {
  const res = await fetchMeReferrals({ page, size }, { silent: !!silent });
  return await RqUnwrap(res, vm);
}

async function fetchMeReferralsSafe(page, size, silent, vm) {
  return await fetchMeR(vm, page, size, silent);
}
</script>

<style scoped>
/* ✅ 你原样式完全保留（我只改了 row-main 的 grid 以适配“等级”列） */

.me {
  --bg0: #050814;
  --bg1: #070b1d;
  --txt: rgba(248, 249, 255, .94);
  --muted: rgba(248, 249, 255, .66);
  --line: rgba(255, 255, 255, .1);

  --orange: #ff9f2f;
  --orange2: #ff6a2a;
  --amber: #ffd08a;

  --shadow: 0 30px 90px rgba(0, 0, 0, .55);
  --shadow2: 0 18px 70px rgba(0, 0, 0, .45);

  min-height: 100vh;
  color: var(--txt);
  background: linear-gradient(180deg, var(--bg1) 0%, var(--bg0) 100%);
  overflow-x: hidden;

  font-family: Inter, -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display",
    "Segoe UI", Roboto, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", Arial, sans-serif;
}

button {
  font: inherit;
  color: inherit;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border: 0;
  outline: none;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.muted {
  opacity: .7;
}

.small {
  font-size: 12px;
}

.unit {
  font-size: 12px;
  opacity: .72;
  font-weight: 900;
  margin-left: 6px;
}

.sep {
  margin: 0 8px;
  opacity: .55;
}

.em {
  color: rgba(255, 208, 138, .98);
  font-weight: 980;
}

/* 背景 */
.bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  background:
    radial-gradient(1000px 700px at 16% 12%, rgba(91, 140, 255, .18), transparent 60%),
    radial-gradient(900px 650px at 86% 18%, rgba(34, 211, 238, .14), transparent 58%),
    radial-gradient(900px 900px at 52% 92%, rgba(170, 110, 255, .12), transparent 62%),
    linear-gradient(180deg, #070b1d 0%, #050814 75%, #04050a 100%);
}

.grid {
  position: absolute;
  inset: -2px;
  background:
    linear-gradient(rgba(255, 255, 255, .05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .035) 1px, transparent 1px);
  background-size: 58px 58px;
  opacity: .08;
  transform: perspective(950px) rotateX(60deg) translateY(-10%);
  transform-origin: top center;
}

.noise {
  position: absolute;
  inset: 0;
  opacity: .09;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.7' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.55'/%3E%3C/svg%3E");
}

.glow {
  position: absolute;
  width: 560px;
  height: 560px;
  border-radius: 999px;
  filter: blur(60px);
  opacity: .28;
}

.g1 {
  left: -220px;
  top: 120px;
  background: rgba(255, 159, 47, .55);
}

.g2 {
  right: -240px;
  top: 60px;
  background: rgba(255, 106, 42, .4);
}

.g3 {
  left: 26%;
  bottom: -300px;
  background: rgba(170, 110, 255, .25);
}

/* 主体 */
.main {
  max-width: 980px;
  margin: 0 auto;
  padding: 14px 14px calc(8px + env(safe-area-inset-bottom));
}

/* 标题 */
.page-hd {
  padding: 6px 2px 12px;
}

.ttl {
  font-size: 20px;
  font-weight: 980;
  letter-spacing: .2px;
}

.sub {
  margin-top: 4px;
  font-size: 12px;
  opacity: .7;
}

/* panel */
.panel {
  border-radius: 22px;
  padding: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, .055), rgba(255, 255, 255, .03));
  border: 1px solid rgba(255, 255, 255, .1);
  box-shadow: var(--shadow2);
  backdrop-filter: blur(18px);
  position: relative;
  overflow: hidden;
}

.panel::after {
  content: "";
  position: absolute;
  inset: -40%;
  background:
    radial-gradient(circle at 30% 30%, rgba(255, 159, 47, .1), transparent 55%),
    radial-gradient(circle at 70% 40%, rgba(255, 106, 42, .08), transparent 55%);
  opacity: .45;
  pointer-events: none;
}

/* hero */
.hero-top {
  display: flex;
  gap: 14px;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.hl,
.hr {
  min-width: 240px;
}

.k {
  font-size: 12px;
  opacity: .7;
}

.mt {
  margin-top: 10px;
}

.v {
  margin-top: 6px;
  font-weight: 950;
  letter-spacing: .2px;
}

.lv {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 159, 47, .35);
  background: rgba(255, 159, 47, .12);
  color: rgba(255, 208, 138, .98);
  font-weight: 980;
  letter-spacing: .25px;
}

.badge.mini {
  height: 26px;
  padding: 0 10px;
  font-size: 12px;
}

.addr {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.copy {
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .12);
  background: rgba(255, 255, 255, .06);
  font-size: 12px;
  white-space: nowrap;
}

.stats {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.sitem {
  border-radius: 16px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, .16);
  border: 1px solid rgba(255, 255, 255, .1);
}

.sk {
  font-size: 12px;
  opacity: .7;
}

.sv {
  margin-top: 6px;
  font-weight: 980;
}

.up {
  color: rgba(255, 208, 138, .96);
}

/* ✅ hint */
.ov-hint {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, .08);
  font-size: 12px;
}

/* progress */
.prog {
  margin-top: 12px;
  border-radius: 18px;
  padding: 12px;
  background: rgba(0, 0, 0, .14);
  border: 1px solid rgba(255, 255, 255, .08);
}

.prog-top {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.pv {
  font-weight: 980;
  letter-spacing: .2px;
}

.bar {
  margin-top: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .07);
  border: 1px solid rgba(255, 255, 255, .1);
  overflow: hidden;
}

.bar-in {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 159, 47, .98), rgba(255, 106, 42, .9));
}

.prog-foot {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.pill2 {
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .12);
  background: rgba(255, 255, 255, .05);
}

/* invite */
.invite {
  margin-top: 14px;
}

.inv-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.inv-box {
  margin-top: 10px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(0, 0, 0, .16);
  border: 1px solid rgba(255, 255, 255, .1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* sec */
.sec {
  margin-top: 18px;
}

.sec-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin: 0 2px 10px;
}

.st {
  font-weight: 950;
  font-size: 18px;
  letter-spacing: .2px;
}

.right {
  font-size: 12px;
}

/* list */
.list {
  padding: 12px;
}

.row {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, .08);
  background: rgba(0, 0, 0, .10);
}

.row+.row {
  margin-top: 10px;
}

/* ✅ 让“地址 / 等级 / 时间”的表头（label）在同一行 */
.row-main {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr auto;
  gap: 12px;
  padding: 12px;
  align-items: start;
  /* 关键：别再 center */
  text-align: left;
}

.row-main.compact .label {
  margin-bottom: 4px;
}

.row-main.compact .val {
  font-weight: 950;
  letter-spacing: .2px;
}

.c1,
.c2,
.cL {
  min-width: 0;
}

.cL .val {
  display: flex;
  align-items: center;
}

/* ✅ 每一列内部自己两行：label / value，并且从顶部对齐 */
.c1,
.cL,
.c2 {
  display: grid;
  grid-template-rows: auto auto;
  align-content: start;
}

/* ✅ 统一 label 的高度/行高，确保三列 label 完全齐线 */
.row-main .label {
  line-height: 14px;
  height: 14px;
  margin-bottom: 6px;
}

/* ✅ “等级”徽章往上提一点（更高一点） */
.cL .val {
  margin-top: -2px;
  /* 想更高：-3px / -4px */
  display: flex;
  align-items: flex-start;
}

.caret {
  width: 10px;
  height: 10px;
  border-right: 2px solid rgba(255, 255, 255, .55);
  border-bottom: 2px solid rgba(255, 255, 255, .55);
  transform: rotate(45deg);
  transition: .18s ease;
  opacity: .9;
  justify-self: end;
}

.caret.up {
  transform: rotate(-135deg);
}

.row-more {
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, .08);
  background: rgba(0, 0, 0, .14);
}

.more-core {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 08px;
}

.kline {
  border-radius: 16px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, .16);
  border: 1px solid rgba(255, 255, 255, .10);
}

.kline .k {
  font-size: 12px;
  opacity: .7;
}

.kline .v {
  margin-top: 6px;
  font-weight: 980;
}

.more-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mcard {
  border-radius: 16px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(255, 255, 255, .08);
}

.mk {
  font-size: 12px;
  opacity: .7;
}

.mv {
  margin-top: 6px;
  font-weight: 950;
}

/* buttons */
.btn {
  height: 44px;
  line-height: 44px;
  border-radius: 14px;
  padding: 0 16px;
  font-weight: 950;
  letter-spacing: .2px;
  border: 1px solid rgba(255, 255, 255, .12);
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease, background .15s ease;
}

.btn:active {
  transform: translateY(1px);
}

.btn.small {
  height: 34px;
  line-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
}

.btn.primary {
  border-color: rgba(255, 159, 47, .38);
  background: linear-gradient(135deg, rgba(255, 159, 47, .98), rgba(255, 106, 42, .92));
  box-shadow: 0 22px 82px rgba(255, 159, 47, .14);
  color: #16120b;
}

.btn:disabled {
  opacity: .55;
  cursor: not-allowed;
}

/* loadmore */
.loadmore {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, .08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: nowrap;
}

.loadmore>div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn.lm {
  flex: 0 0 auto;
  height: 36px;
  line-height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(255, 255, 255, .10);
  color: rgba(247, 248, 255, .86);
  white-space: nowrap;
}

.btn.lm:disabled {
  opacity: .6;
  transform: none;
}

/* ✅ skeleton / empty */
.empty {
  padding: 18px 6px;
  text-align: center;
  font-size: 13px;
}

.skeleton .sk {
  height: 66px;
  border-radius: 18px;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(255, 255, 255, .08);
}

.skeleton .sk+.sk {
  margin-top: 10px;
}

/* mobile */
@media (max-width: 520px) {
  .more-core {
    grid-template-columns: 1fr 1fr;
  }

  .row-main {
    grid-template-columns: 1fr auto 1fr auto;
  }
}

/* Web / PC 字体修复 */
@media (min-width: 900px) {
  .me {
    font-size: 14px;
    line-height: 1.4;
  }

  .ttl {
    font-size: 22px;
  }

  .sub {
    font-size: 12px;
  }

  .hr .addr {
    font-size: clamp(22px, 2.2vw, 34px);
    line-height: 1.1;
    letter-spacing: .6px;
  }

  .hr .addr .copy {
    font-size: 12px;
  }

  .inv-box {
    font-size: 15px;
    line-height: 22px;
    letter-spacing: .2px;
  }

  .row-main {
    padding: 16px 18px;
    gap: 16px;
  }

  .row-main.compact .label {
    font-size: 12px;
    line-height: 16px;
    opacity: .65;
    margin-bottom: 6px;
  }

  .row-main.compact .val {
    font-size: clamp(18px, 2.0vw, 26px);
    line-height: 1.1;
    letter-spacing: .6px;
    font-weight: 980;
  }

  .row-main .c2 .val {
    opacity: .62;
  }

  .kline .v {
    font-size: 18px;
    line-height: 24px;
  }

  .btn {
    font-size: 14px;
  }

  .btn.lm {
    height: 34px;
    line-height: 34px;
    font-size: 12px;
    padding: 0 14px;
    border-radius: 999px;
  }

  .loadmore>div {
    max-width: 60%;
  }
}

.node-badge{
  display:inline-flex;
  align-items:center;
  gap:6px;
  height:28px;
  padding:0 11px;
  border-radius:999px;
  border:1px solid rgba(53,245,182,.32);
  background:linear-gradient(135deg,rgba(53,245,182,.16),rgba(255,159,47,.08));
  color:rgba(191,255,233,.96);
  font-size:12px;
  font-weight:950;
  box-shadow:0 10px 34px rgba(53,245,182,.08);
}

.node-dot{
  width:7px;
  height:7px;
  border-radius:999px;
  background:#35f5b6;
  box-shadow:0 0 0 3px rgba(53,245,182,.14);
}

.invite.locked {
  border-color: rgba(255, 159, 47, .16);
  background:
    radial-gradient(520px 180px at 12% 0%, rgba(255, 159, 47, .10), transparent 60%),
    linear-gradient(180deg, rgba(255, 255, 255, .055), rgba(255, 255, 255, .03));
}

.inviteLocked {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.inviteLockIcon {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(255, 159, 47, .12);
  border: 1px solid rgba(255, 159, 47, .24);
  flex-shrink: 0;
}

.inviteLockBody {
  flex: 1;
  min-width: 0;
}

.inviteLockTitle {
  font-size: 14px;
  font-weight: 980;
  color: rgba(248, 249, 255, .96);
}

.inviteLockSub {
  margin-top: 5px;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(248, 249, 255, .62);
}

@media (max-width: 520px) {
  .inviteLocked {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .inviteLocked .btn {
    width: 100%;
  }
}
</style>


